import { forwardRef, useId, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FieldNote } from './FieldNote';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: ReactNode;
  labelClassName?: string;
  error?: string;
  hint?: ReactNode;
  leftIcon?: ReactNode;
  /** Adds a show/hide toggle. Use instead of type="password". */
  isPassword?: boolean;
  /**
   * Keeps the label for screen readers but takes it off the screen — for a row
   * of repeated fields where a visible label on every one is noise. The label
   * must still say which field this is ("Core subject 3"), never just "Name".
   */
  hideLabel?: boolean;
  /**
   * Values to offer as the person types, drawn as the browser's own dropdown
   * under the box. Still free text: anything not in the list can be typed.
   */
  suggestions?: readonly string[];
  required?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    labelClassName,
    error,
    hint,
    leftIcon,
    isPassword = false,
    hideLabel = false,
    suggestions,
    required = false,
    className,
    id,
    type,
    ...props
  },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;
  const suggestionsId = `${inputId}-suggestions`;
  const hasSuggestions = Boolean(suggestions && suggestions.length > 0);

  const [isRevealed, setIsRevealed] = useState(false);
  const resolvedType = isPassword ? (isRevealed ? 'text' : 'password') : type;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className={cn('lbl', labelClassName, hideLabel && 'sr-only')}>
        {label}
        {required && <span className="text-red-500 ml-0.5" aria-hidden="true">*</span>}
      </label>

      <div className="relative">
        {leftIcon && (
          <span
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-3"
            aria-hidden
          >
            {leftIcon}
          </span>
        )}

        <input
          ref={ref}
          id={inputId}
          type={resolvedType}
          // Screen readers announce the error and skip the hint once invalid.
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : hint ? hintId : undefined}
          list={hasSuggestions ? suggestionsId : undefined}
          className={cn(
            'h-10 w-full rounded-field border bg-surface px-3 text-sm text-ink py-2',
            'placeholder:text-ink-3 placeholder:text-sm',
            'transition-colors duration-150',
            'focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300 dark:focus:border-slate-600 dark:focus:ring-slate-700',
            'disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-ink-3',
            error ? 'border-crit focus:border-crit focus:ring-crit/25' : 'border-line-strong',
            className,
            leftIcon && '!pl-10',
            isPassword && '!pr-11',
          )}
          {...props}
        />

        {hasSuggestions && (
          <datalist id={suggestionsId}>
            {suggestions?.map((value) => <option key={value} value={value} />)}
          </datalist>
        )}

        {isPassword && (
          <button
            type="button"
            onClick={() => setIsRevealed((value) => !value)}
            aria-label={isRevealed ? 'Hide password' : 'Show password'}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-ink-3 transition-colors hover:text-accent"
            tabIndex={-1}
          >
            {isRevealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        )}
      </div>

      <FieldNote error={error} hint={hint} errorId={errorId} hintId={hintId} />
    </div>
  );
});
